import { print } from 'graphql';
import type { RxCollection, RxDocument, RxJsonSchema } from 'rxdb';
import { MigrationStrategies } from 'rxdb';

import { gql } from '../../graphql/Client';
import { BaseEntity } from '../../models/BaseEntity';
import { generatePullQuery, PullQueryContext } from '../queryBuilders/generatePullQuery';
import RxdbCollectionName from '../rxdbCollectionName';
import RxdbManager from '../RxdbManager';
import { defaultDocAllTime } from '../Shared/defaultDoc';
import { CheckpointType } from '../types';

export interface InspectionSeverity extends BaseEntity {
  readonly inspectionId: string;
  readonly description: string;
  readonly lowSeverity: number;
  readonly highSeverity: number;
  readonly miningMethod: string;
  readonly color: string;
}
export type InspectionSeverityCollection = RxCollection<InspectionSeverity> | null;
export type InspectionSeverityDocument = RxDocument<InspectionSeverity>;

export const inspectionSeveritySchema: RxJsonSchema<InspectionSeverity> = {
  type: 'object',
  version: 4,
  description: 'describes a InspectionSeverity object',
  primaryKey: 'id',
  properties: {
    id: { type: 'string', maxLength: 36 },
    inspectionId: {
      ref: RxdbCollectionName.INSPECTIONS,
      type: ['string', 'null'],
    },
    description: { type: 'string' },
    lowSeverity: { type: 'number' },
    highSeverity: { type: 'number' },
    isDeleted: { type: 'boolean' },
    updatedAt: { type: 'number' },
    version: { type: 'number' },
    miningMethod: { type: ['string', 'null'] },
    color: { type: 'string' },
  },
};

export const inspectionSeverityMigrationStrategies: MigrationStrategies = {
  1: (oldDoc: InspectionSeverityDocument) => ({ ...oldDoc }),
  2: (oldDoc: InspectionSeverityDocument) => {
    return { ...oldDoc };
  },
  3: (oldDoc: InspectionSeverityDocument) => ({ ...oldDoc }),
  4: (oldDoc: InspectionSeverityDocument) => ({ ...oldDoc }),
};

const inspectionSeverityFeedQuery = print(gql`
  query inspectionSeverityFeed($siteId: ID!, $lastUpdateAt: Float!, $limit: Int!) {
    inspectionSeverityFeed(lastUpdateAt: $lastUpdateAt, limit: $limit, siteId: $siteId) {
      id
      inspectionId
      description
      lowSeverity
      highSeverity
      isDeleted
      color
      updatedAt
      version
      miningMethod
    }
  }
`);

const basePullQueryBuilder = generatePullQuery(
  inspectionSeverityFeedQuery,
  PullQueryContext.Site,
  defaultDocAllTime,
);
let initialPullQuery = true;

export const inspectionSeverityPullQueryBuilder = (
  lastPulledCheckpoint: CheckpointType | undefined,
  limit: number,
) => {
  // HACK: Force initial re-pull of all inspection severity data to ensure new 'color' field is populated.
  // This addresses the issue where existing records in RxDB don't get updated when new fields are added,
  // because the backend records haven't actually been "updated" (their updatedAt timestamp remains the same).
  // The migration strategy (version 2) only applies to existing local data but doesn't force a server re-sync.
  // By ignoring the lastPulledCheckpoint on the first query, we force RxDB to re-pull all existing records,
  // which will then include the new 'color' field and trigger the migration to set default values.
  // This ensures users get the new field without requiring a full RxDB reset.
  // TODO: remove this once all clients have been updated and re-synced the inspection severity data
  if (initialPullQuery) {
    initialPullQuery = false;
    // Instead of passing null, create a new checkpoint object.
    // This forces a full re-sync by setting updatedAt to 1,
    // while preserving the ID of the last document for context.
    const forcedSyncCheckpoint: CheckpointType = {
      id: lastPulledCheckpoint?.id || defaultDocAllTime.id,
      updatedAt: 1,
    };
    return basePullQueryBuilder(forcedSyncCheckpoint, limit);
  }
  return basePullQueryBuilder(lastPulledCheckpoint, limit);
};

// We can remove this inspectionSeverityPullResponseModifier
// once all clients have been updated and re-synced the inspection severity data
// Its purpose is to handle the response from the GraphQL API when collection has some records
// which need to be deleted (isDeleted: true)
// RxDB does not automatically handle this case properly, so we need to implement a custom response modifier.
export const inspectionSeverityPullResponseModifier = async (
  plainResponse: any,
  _origin: 'handler' | 'stream',
  requestCheckpoint: CheckpointType | undefined,
): Promise<{ documents: any; checkpoint: CheckpointType }> => {
  let documents: InspectionSeverity[];
  if (plainResponse && plainResponse.data) {
    const dataKey = Object.keys(plainResponse.data)[0];
    documents = plainResponse.data[dataKey] || [];
  } else if (Array.isArray(plainResponse)) {
    documents = plainResponse;
  } else {
    documents = [];
  }

  // BUG WORKAROUND:
  // Separate the documents into two batches: one for upserts and one for deletes.
  // This avoids a potential RxDB bug when processing a mixed batch.
  const docsToUpsert = documents.filter(doc => !doc.isDeleted);

  const docsToDelete = documents.filter(doc => doc.isDeleted);

  // Process the deletes first by directly calling the collection's bulkRemove method.
  // This is an "out-of-band" operation that handles the deletes immediately.
  if (docsToDelete.length > 0) {
    const rxdb = RxdbManager.instance?.db;
    const collection = rxdb?.collections[RxdbCollectionName.INSPECTION_SEVERITY];
    const idsToDelete = docsToDelete.map(doc => doc.id);
    if (collection) {
      // Only remove documents that actually exist locally
      // Server may report deleted docs that were never synced to this client
      const existingDocs = await collection.findByIds(idsToDelete).exec();
      const existingIds = Array.from(existingDocs.keys());

      if (existingIds.length > 0) {
        // We don't await this, let it run in the background.
        collection.bulkRemove(existingIds);
      }
    }
  }

  // Now, return ONLY the documents that need to be created or updated.
  // The checkpoint is calculated from the original full batch to ensure sync continues correctly.
  let checkpoint: CheckpointType;
  if (documents.length > 0) {
    const lastDocInOriginalBatch = documents[documents.length - 1];
    checkpoint = {
      id: lastDocInOriginalBatch.id,
      updatedAt: lastDocInOriginalBatch.updatedAt,
    };
  } else {
    checkpoint = requestCheckpoint ?? { id: '', updatedAt: 0 };
  }

  return {
    documents: docsToUpsert,
    checkpoint,
  };
};
